'use client';

import { ProductCard } from '@components/Product/ProductCard';
import { ProductCardV2 } from '@components/Product/ProductCard.v2';
import { useEntitlements } from '@contexts/EntitlementsContext';
import { useProductTags } from '@contexts/ProductTagsContext';
import { useFlags } from '@lib/flags/amplitude/client';
import {
  FEATURE_FLAG_ON,
  FLAG_TREATMENT,
  Flags,
} from '@lib/flags/flagConstants';
import { longTextToProductIds } from '@lib/util/longTextToProductIds';
import { ToggleMoreItemsButton } from '@ui-components/components/page-building/SubCategorySelector/ToggleMoreItemsButton';
import classNames from 'classnames';
import { CaretDown, CaretUp } from 'phosphor-icons';
import { useState } from 'react';
import { getLocaleFromUrl } from 'utils';
import {
  mapToProductCardProps,
  mapToProductCardV2Props,
} from '../ProductRow/mapToProductCardProps';
import { usePackagesByIds } from '../ProductRow/usePackagesByIds';

type FeaturedProducts = {
  title: string;
  featuredProductIds: string;
  showMoreItemsLabel?: string;
  showLessItemsLabel?: string;
};

export const FeaturedProducts = ({
  title = 'Featured Products',
  featuredProductIds,
  showLessItemsLabel = 'Show less',
  showMoreItemsLabel = 'Show {{number}} more',
}: FeaturedProducts) => {
  const productIDs = longTextToProductIds(featuredProductIds);
  const { data: products, error, isLoading } = usePackagesByIds(productIDs);
  const MAX_INITIAL_DISPLAY = 4;
  const [showMoreItems, setShowMoreItems] = useState(false);
  const productTags = useProductTags();
  const { getUserEntitlement } = useEntitlements();
  const locale = getLocaleFromUrl();
  const { flags } = useFlags();

  const isNewSpacingEnabled =
    flags[Flags.UI_AND_MISSING_FILTERS]?.value === FLAG_TREATMENT;

  const isProductCardV2Enabled =
    // CARD_DESIGN FLAG controls the development
    // TODO: remove the CARD_DESIGN flag check when the CARD DENSITY is implemented
    flags[Flags.CARD_DESIGN]?.value === FEATURE_FLAG_ON &&
    // UI_AND_MISSING_FILTERS FLAG controls the experiment
    isNewSpacingEnabled;

  if (isLoading || error) {
    return null;
  }

  if (!products || products?.length === 0) {
    return null;
  }

  const formattedProductsV2 = isProductCardV2Enabled
    ? mapToProductCardV2Props(products, getUserEntitlement)
    : [];
  const formattedProductsV1 = isProductCardV2Enabled
    ? []
    : mapToProductCardProps(products, productTags, getUserEntitlement);

  const hasMoreItemsThanDisplayMax = products.length > MAX_INITIAL_DISPLAY;
  const excessItems = products.length - MAX_INITIAL_DISPLAY;

  const handleToggleShowMoreItems = () => {
    setShowMoreItems(!showMoreItems);
  };

  return (
    <div className="container relative mt-4 md:mt-8">
      <h2 className="header-small mb-3 lg:mb-2">{title}</h2>
      <div
        className={classNames(
          'hide-scrollbar flex grid-cols-4 gap-4 overflow-x-scroll md:grid md:overflow-visible',
          !isNewSpacingEnabled && 'lg:gap-5 lg:gap-y-2'
        )}
      >
        {isProductCardV2Enabled
          ? formattedProductsV2.map((product, index) => (
              <ProductCardV2
                key={product.productId}
                className={classNames(
                  'mr-0 md:min-w-full',
                  index >= MAX_INITIAL_DISPLAY && !showMoreItems && 'md:hidden'
                )}
                locale={locale}
                {...product}
              />
            ))
          : formattedProductsV1.map((product, index) => (
              <ProductCard
                key={product.productId}
                className={classNames(
                  'mr-0 md:min-w-full',
                  index >= MAX_INITIAL_DISPLAY && !showMoreItems && 'md:hidden'
                )}
                locale={locale}
                {...product}
              />
            ))}
      </div>
      {hasMoreItemsThanDisplayMax && (
        <div
          className={classNames('mt-6 hidden justify-center md:flex')}
          data-test="product-row-toggle-more"
        >
          <ToggleMoreItemsButton
            excessItems={excessItems}
            onClick={handleToggleShowMoreItems}
            label={
              showMoreItems
                ? showLessItemsLabel
                : showMoreItemsLabel.replace(
                    '{{number}}',
                    excessItems.toString()
                  )
            }
            icon={showMoreItems ? CaretUp : CaretDown}
          />
        </div>
      )}
    </div>
  );
};
